import { useProjectId } from '@config/config.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { DownloadFile } from '@file/DownloadFile';
import { Icon, Tooltip } from '@gcl/react-core';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { ETransmittalType } from '@transmittal/transmittal.constants';
import React from 'react';
import { useDeleteTransmittalItemRevision } from './transmittalItemTableForm.hooks';

interface ITransmittalItemTableRowTransmittalRevision {
  inUse: boolean;
  attachmentId?: number;
  transmittalType?: ETransmittalType;
  downloadFileText?: string;
  itemRevisionId?: number;
  transmittalId: number;
  hasDeletePermission: boolean;
  disabledDeleteRevision: boolean;
  queryKey: QueryKey;
  setRowUploading: (index: number, isUploading: boolean) => void;
  rowIndex: number;
  tableIndex: number;
  prevRevision?: string;
}
export const TransmittalItemTableRowTransmittalRevision = ({
  inUse,
  attachmentId,
  transmittalType,
  downloadFileText,
  itemRevisionId,
  transmittalId,
  hasDeletePermission,
  queryKey,
  setRowUploading,
  rowIndex,
  tableIndex,
  prevRevision,
  disabledDeleteRevision,
}: ITransmittalItemTableRowTransmittalRevision) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  const { renderDeleteRevisionModal, openDeleteRevisionModal, useDeleteProjectTransmittalItemRevisionMutation } =
    useDeleteTransmittalItemRevision(
      transmittalId,
      prevRevision,
      downloadFileText,
      itemRevisionId,
      rowIndex,
      setRowUploading,
    );

  const {
    mutate: mutateDeleteProjectTransmittalItemRevision,
    isSuccess: isSuccessDeleteProjectTransmittalItemRevision,
    isLoading: isLoadingDeleteProjectTransmittalItemRevision,
    isError: isErrorDeleteProjectTransmittalItemRevision,
    reset: resetDeleteProjectTransmittalItemRevision,
    error: errorDeleteProjectTransmittalItemRevision,
  } = useDeleteProjectTransmittalItemRevisionMutation;

  if (!inUse) {
    return (
      <div className="gcl-gems-transmittal-item-download-column">
        <Icon iconName="LockFill" className="gcl-ic-btn-md gcl-gems-transmittal-item-not-in-use" />
      </div>
    );
  }
  return (
    <>
      <ApiErrorDisplay
        errors={[
          {
            error: errorDeleteProjectTransmittalItemRevision,
            genericError: 'Delete transmittal item revision failed',
            reset: resetDeleteProjectTransmittalItemRevision,
          },
        ]}
        callbackAfterToast={() => {
          setRowUploading(rowIndex, false);
        }}
      />
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessDeleteProjectTransmittalItemRevision,
            message: 'Delete transmittal item revision success',
            reset: resetDeleteProjectTransmittalItemRevision,
          },
        ]}
        callbackAfterToasted={() => {
          queryClient.invalidateQueries(queryKey);
          setRowUploading(rowIndex, false);
        }}
      />
      <div className="gcl-gems-transmittal-item-download-column gcl-gems-transmittal-item-transmittal-revision-container">
        {!attachmentId ? (
          <Tooltip overlay="Not issued">
            <Icon iconName="DashCircle" className="gcl-gems-transmittal-item-download-column-not-issued-icon" />
          </Tooltip>
        ) : (
          <>
            <DownloadFile
              shouldChopICEApprovalOnly={transmittalType !== ETransmittalType.SITE}
              shouldNotChop={transmittalType === ETransmittalType.SITE}
              text={downloadFileText}
              attachmentId={attachmentId}
              projectId={projectId}
            />
            {hasDeletePermission &&
              (transmittalType === ETransmittalType.DESIGNER || transmittalType === ETransmittalType.SITE) && (
                <>
                  <Tooltip
                    overlay={`Delete transmittal item revision ${downloadFileText}`}
                    id="delete-transmittal-item-revision"
                    key={`delete-transmittal-item-revision-${attachmentId}`}
                    placement="left"
                  >
                    <Icon
                      data-testid={`gcl-gems-transmitta-item-table-delete-revision-btn-table-${tableIndex}-row-${rowIndex}`}
                      iconName="Trash"
                      disabled={disabledDeleteRevision}
                      className="gcl-ic-btn-lg gcl-fc-danger-400 gcl-ml-xs"
                      onClick={openDeleteRevisionModal}
                    />
                  </Tooltip>
                  {renderDeleteRevisionModal(
                    isLoadingDeleteProjectTransmittalItemRevision,
                    isErrorDeleteProjectTransmittalItemRevision,
                    isSuccessDeleteProjectTransmittalItemRevision,
                  )}
                </>
              )}
          </>
        )}
      </div>
    </>
  );
};
